/**
 * Normalised EIP-1193 wallet errors (#687). Wallets disagree on where the "real" code lives:
 *
 *   MetaMask desktop      { code: 4902 }                                 unknown chain
 *   MetaMask mobile       { code: -32603, data: { originalError: { code: 4902 } } }
 *   some wallets          { code: -32603, message: 'Unrecognized chain ID ...' }   (4902 only in the text)
 *   ethers/viem wrappers  { code: ..., cause: { code: 4902 } } or { error: { code } }
 *   Rainbow               unknown chain = { code: -32600, 'Chain Id not supported' }; add rejected = -32003
 *   Rabby                 chain mismatch = { code: -32602, 'chainId should be same as current chainId' }
 *   Brave                 chain mismatch = { code: -32603, 'chainId 1337 is not current network' }
 *
 * walletErrorCodes() walks all of those (depth-limited) so callers can ask "is this a 4902?" once.
 * See docs/research/WALLETS.md for the per-wallet table.
 */

export const USER_REJECTED = 4001;
export const UNAUTHORIZED = 4100;
export const UNSUPPORTED_METHOD = 4200;
export const DISCONNECTED = 4900;
export const CHAIN_DISCONNECTED = 4901;
export const UNRECOGNIZED_CHAIN = 4902;
export const INVALID_PARAMS = -32602;
export const INTERNAL_ERROR = -32603;
export const METHOD_NOT_FOUND = -32601;
export const REQUEST_PENDING = -32002;
/** EIP-1474 "transaction rejected": Rainbow uses it for a rejected wallet_addEthereumChain. */
export const TX_REJECTED = -32003;

type Obj = Record<string, unknown>;

function isObj(v: unknown): v is Obj {
  return typeof v === 'object' && v !== null;
}

function asCode(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isInteger(v)) return v;
  if (typeof v === 'string' && /^-?\d+$/.test(v)) return Number(v);
  return undefined;
}

/** Nested error objects to inspect, outermost first. */
function chain(e: unknown): Obj[] {
  const out: Obj[] = [];
  const seen = new Set<unknown>();
  const walk = (v: unknown, depth: number) => {
    if (!isObj(v) || seen.has(v) || depth > 4) return;
    seen.add(v);
    out.push(v);
    const data = v.data;
    if (isObj(data)) {
      walk(data.originalError, depth + 1);
      if (asCode(data.code) !== undefined) walk(data, depth + 1);
    }
    walk(v.cause, depth + 1);
    walk(v.error, depth + 1);
  };
  walk(e, 0);
  return out;
}

/** Every numeric EIP-1193 / JSON-RPC code found on the error or its nested originals (outermost first, unique). */
export function walletErrorCodes(e: unknown): number[] {
  const codes: number[] = [];
  for (const o of chain(e)) {
    const c = asCode(o.code);
    if (c !== undefined && !codes.includes(c)) codes.push(c);
  }
  return codes;
}

/** All messages on the error chain, joined (for pattern checks and display). */
export function walletErrorText(e: unknown): string {
  if (typeof e === 'string') return e;
  const parts: string[] = [];
  for (const o of chain(e)) {
    if (typeof o.message === 'string' && o.message && !parts.includes(o.message)) parts.push(o.message);
  }
  if (!parts.length && e instanceof Error) parts.push(e.message);
  return parts.join(' · ');
}

/** Best single human message (outermost non-empty one). */
export function walletErrorMessage(e: unknown): string {
  if (typeof e === 'string') return e;
  for (const o of chain(e)) if (typeof o.message === 'string' && o.message) return o.message;
  return e instanceof Error ? e.message : String(e);
}

/** 4001 anywhere in the chain, or a message-only rejection ("User denied message signature", "User rejected"). */
export function isUserRejection(e: unknown): boolean {
  const codes = walletErrorCodes(e);
  if (codes.includes(USER_REJECTED) || codes.includes(TX_REJECTED)) return true;
  // ACTION_REJECTED: ethers v6 wrapper; "cancel(l)ed by user": Coinbase / OKX / Trust variants.
  return /user (rejected|denied|cancel)|rejected by (the )?user|cancel+ed by (the )?user|ACTION_REJECTED/i.test(
    walletErrorText(e),
  );
}

/** "Chain not added yet": 4902 (possibly nested), or a generic code whose text says so. */
export function isUnknownChain(e: unknown): boolean {
  if (walletErrorCodes(e).includes(UNRECOGNIZED_CHAIN)) return true;
  return /unrecognized chain|unknown chain|chain (id )?.{0,24}not (been )?(added|supported|configured|found)|try adding the chain|wallet_addEthereumChain first|no chain with id/i.test(
    walletErrorText(e),
  );
}

/** The wallet refused to sign because the typed-data domain chainId is not the active chain. */
export function isChainMismatch(e: unknown): boolean {
  return /chain ?id.{0,40}(must match|mismatch|does not match|doesn't match|not match|should be (the )?same|is not (the )?current network)|(active|current) chain.{0,40}(must match|mismatch|does not match)|chainid.{0,8}(in|of) (the )?domain/i.test(
    walletErrorText(e),
  );
}

/** The wallet does not implement the method (4200 / -32601 / text). */
export function isUnsupportedMethod(e: unknown): boolean {
  const c = walletErrorCodes(e);
  if (c.includes(UNSUPPORTED_METHOD) || c.includes(METHOD_NOT_FOUND)) return true;
  return /(method|request).{0,30}(not supported|unsupported|not implemented|does not exist)/i.test(walletErrorText(e));
}

/** A previous request is still waiting in the wallet (-32002). */
export function isRequestPending(e: unknown): boolean {
  return walletErrorCodes(e).includes(REQUEST_PENDING) || /already pending/i.test(walletErrorText(e));
}
